"use client";

import * as React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "./theme-provider";
import { Toaster } from "sonner";
import { useAuthLocaleSync } from "@/lib/hooks/useAuthLocaleSync";

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,        // 5 minutos - datos considerados frescos
        gcTime: 30 * 60 * 1000,          // 30 minutos en caché
        retry: 1,
        refetchOnWindowFocus: false,     // Evita error "undefined" al volver a la ventana
        refetchOnReconnect: false,       // Evita refetch al reconectar
      },
    },
  });
}

// Variable para el cliente del navegador (singleton en cliente)
let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (typeof window === "undefined") {
    // Servidor: siempre crear nuevo cliente
    return makeQueryClient();
  } else {
    // Cliente: reusar el mismo cliente
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

interface ProvidersProps {
  children: React.ReactNode;
}

/**
 * Hijo interno para poder colgar hooks que dependen del auth-store (que
 * necesita estar montado). No renderiza nada; solo dispara efectos.
 */
const AuthEffects = () => {
  useAuthLocaleSync();
  return null;
};

export const Providers = ({ children }: ProvidersProps) => {
  const queryClient = getQueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
      >
        <AuthEffects />
        {children}
        <Toaster position="top-right" richColors />
      </ThemeProvider>
    </QueryClientProvider>
  );
};
